/**
 * ChatComposer — the chat input <form> rendered at the bottom of ChatBox.
 *
 * Extracted verbatim from ChatBox.tsx (Phase 3 leaf extraction). Presentational
 * only: renders the slash-command autocomplete dropdown, the focused-sections
 * chip row (FocusChipRow), the attachment rails (AttachmentRail), the optional
 * voice mic-toggle anchor, and the main chat-input container (ghost overlay,
 * textarea, hidden file input + upload button, mic/dictation buttons, and the
 * stop/send button). ALL state lives in ChatBox; this component receives every
 * value/handler/ref it reads as explicit props.
 *
 * The channels-pane guard that wraps this <form> on mobile stays in ChatBox —
 * only the <form> subtree moved here.
 */
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { AudioWaveform } from '@/features/chat/components/AudioWaveform';
import { MicToggleButton } from '@/features/voice/MicToggleButton';
import { getMatchingCommands, type CommandInfo } from '@/features/chat/utils/command-parser';
import { MentionDropdown } from './MentionDropdown';
import type { MentionCandidate } from '@/features/chat/hooks/useMentionAutocomplete';
import FocusChipRow, { type FocusedSection } from './FocusChipRow';
import { AttachmentRail } from './AttachmentRail';
import { ChatScopeBar } from './chatbox/ChatScopeBar';
import type { PastedAttachment, PendingAttachment } from './chatbox/types';

/** Structural subset of useVoiceMode()'s return that ChatComposer reads.
 *  Kept local so the leaf doesn't depend on the (currently unexported)
 *  UseVoiceModeReturn interface in useVoiceMode.ts. */
interface ChatComposerVoiceMode {
  isActive: boolean;
  isMicLive: boolean;
  toggleMic: () => void;
  interimTranscript: string;
  phase: 'idle' | 'listening' | 'processing' | 'speaking';
  stopSpeaking: () => void;
}

export interface ChatComposerProps {
  handleSubmit: (e: React.FormEvent) => void | Promise<void>;
  showCommandDropdown: boolean;
  commandMatches: CommandInfo[];
  selectedCommandIndex: number;
  setShowCommandDropdown: Dispatch<SetStateAction<boolean>>;
  setCommandMatches: Dispatch<SetStateAction<CommandInfo[]>>;
  setSelectedCommandIndex: Dispatch<SetStateAction<number>>;
  setInputValue: Dispatch<SetStateAction<string>>;
  chatInputRef: RefObject<HTMLTextAreaElement | null>;
  inputValue: string;
  resizeTextarea: () => void;
  isAnyListening: boolean;
  activeInterimTranscript: string;
  isListening: boolean;
  interimTranscript: string;
  appendDictation: (prevRaw: string, nextRaw: string) => string;
  voiceMode: ChatComposerVoiceMode;
  placeholder: string;
  activeConversation: unknown;
  hasNoConversations: boolean;
  isLoading: boolean;
  isStreaming: boolean;
  isReadOnly: boolean;
  isImporting: boolean;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handlePaste: (e: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  isMobile: boolean;
  shouldShowAnyPane: boolean;
  setMobileView: (view: 'chat' | 'module') => void;
  sessionId: string | undefined;
  fileInputRef: RefObject<HTMLInputElement | null>;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  analyser: AnalyserNode | null;
  cancelVoiceInput: () => void;
  micGesture: {
    onPointerDown: () => void;
    onPointerUp: () => void;
    onPointerLeave: () => void;
  };
  stopStreaming: () => void;
  lastSentRef: RefObject<{ text: string; at: number } | null>;
  pendingAttachments: PendingAttachment[];
  selectedEditSections: FocusedSection[];
  setSelectedEditSections: Dispatch<SetStateAction<FocusedSection[]>>;
  selectedChatPaneModuleTitle: string | null;
  pastedAttachments: PastedAttachment[];
  expandedPastedAttachmentIds: Set<string>;
  setExpandedPastedAttachmentIds: Dispatch<SetStateAction<Set<string>>>;
  setPastedAttachments: Dispatch<SetStateAction<PastedAttachment[]>>;
  setPendingAttachments: Dispatch<SetStateAction<PendingAttachment[]>>;
  // During onboarding the beat transcript drives the flow; the composer is
  // locked until the tour finishes.
  onboardingActive: boolean;
  // @-mention autocomplete (issue #1457). State owned by useMentionAutocomplete
  // in ChatBox; this leaf renders the dropdown and fires detection on change.
  showMentionDropdown: boolean;
  mentionMatches: MentionCandidate[];
  selectedMentionIndex: number;
  isMentionLoading: boolean;
  handleMentionInput: (value: string, caret: number) => boolean;
  selectMention: (index?: number) => void;
  setSelectedMentionIndex: (index: number) => void;
}

export function ChatComposer({
  handleSubmit,
  showCommandDropdown,
  commandMatches,
  selectedCommandIndex,
  setShowCommandDropdown,
  setCommandMatches,
  setSelectedCommandIndex,
  setInputValue,
  chatInputRef,
  inputValue,
  resizeTextarea,
  isAnyListening,
  activeInterimTranscript,
  isListening,
  interimTranscript,
  appendDictation,
  voiceMode,
  placeholder,
  activeConversation,
  hasNoConversations,
  isLoading,
  isStreaming,
  isReadOnly,
  isImporting,
  handleKeyDown,
  handlePaste,
  isMobile,
  shouldShowAnyPane,
  setMobileView,
  sessionId,
  fileInputRef,
  handleFileSelect,
  analyser,
  cancelVoiceInput,
  micGesture,
  stopStreaming,
  lastSentRef,
  pendingAttachments,
  selectedEditSections,
  setSelectedEditSections,
  selectedChatPaneModuleTitle,
  pastedAttachments,
  expandedPastedAttachmentIds,
  setExpandedPastedAttachmentIds,
  setPastedAttachments,
  setPendingAttachments,
  onboardingActive,
  showMentionDropdown,
  mentionMatches,
  selectedMentionIndex,
  isMentionLoading,
  handleMentionInput,
  selectMention,
  setSelectedMentionIndex,
}: ChatComposerProps) {
  return (
        <form onSubmit={handleSubmit} className="chat-form">
        {/* Agents Phase 4 (piece 3): scope-indicator chip above the input — renders only
            when scoped to a run (null = orchestrator). Context-driven, visual scoping only. */}
        <ChatScopeBar />
        {/* Command Autocomplete Dropdown - positioned above input */}
        {showCommandDropdown && commandMatches.length > 0 && (
          <div className="command-autocomplete-dropdown">
            {commandMatches.map((cmd, index) => (
              <button
                key={cmd.name}
                type="button"
                className={`command-autocomplete-item ${index === selectedCommandIndex ? 'selected' : ''}`}
                onClick={() => {
                  setShowCommandDropdown(false);
                  setCommandMatches([]);
                  if (cmd.hasArgs) {
                    // Command takes arguments - add space for user to type
                    setInputValue('/' + cmd.name + ' ');
                    chatInputRef.current?.focus();
                  } else {
                    // Command has no args - submit immediately
                    setInputValue('/' + cmd.name);
                    setTimeout(() => {
                      const form = chatInputRef.current?.closest('form');
                      form?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
                    }, 0);
                  }
                }}
                onMouseEnter={() => setSelectedCommandIndex(index)}
              >
                <span className="command-name">/{cmd.name}</span>
                <span className="command-description">{cmd.description}</span>
              </button>
            ))}
          </div>
        )}
        {/* @-Mention Autocomplete Dropdown (issue #1457) — positioned above input,
            same anchoring as the command dropdown. Backed by hybridSearch. */}
        <MentionDropdown
          show={showMentionDropdown}
          matches={mentionMatches}
          selectedIndex={selectedMentionIndex}
          isLoading={isMentionLoading}
          onSelect={selectMention}
          onHighlight={setSelectedMentionIndex}
        />
        {/* Focused sections chip row — data-driven by the inspector selection.
            See FocusChipRow.tsx for the controlled component. */}
        <FocusChipRow
          sections={selectedEditSections}
          pageTitle={selectedChatPaneModuleTitle || undefined}
          onChange={setSelectedEditSections}
        />
        <AttachmentRail
          pastedAttachments={pastedAttachments}
          expandedPastedAttachmentIds={expandedPastedAttachmentIds}
          setExpandedPastedAttachmentIds={setExpandedPastedAttachmentIds}
          setPastedAttachments={setPastedAttachments}
          pendingAttachments={pendingAttachments}
          setPendingAttachments={setPendingAttachments}
          isImporting={isImporting}
        />
        {voiceMode.isActive && (
          <div className="voice-mic-toggle-anchor" onClick={(e) => e.stopPropagation()}>
            <MicToggleButton isLive={voiceMode.isMicLive} onToggle={voiceMode.toggleMic} />
          </div>
        )}
        <div className={`chat-input-container${isAnyListening ? ' dictating' : ''}`}>
          {isAnyListening && activeInterimTranscript && (
            <div className="chat-input-ghost" aria-hidden="true">
              <span className="chat-input-ghost-real">{inputValue}</span>
              <span className="chat-input-ghost-interim">{isListening ? appendDictation(inputValue, interimTranscript).slice(inputValue.length) : voiceMode.interimTranscript}</span>
            </div>
          )}
          <textarea
            ref={chatInputRef}
            key={sessionId || 'no-session'}
            name="message"
            value={inputValue}
            onChange={(e) => {
              const value = e.target.value;
              setInputValue(value);

              // @-mention detection (issue #1457). Runs first so an active
              // mention suppresses the slash-command dropdown (they never show
              // together). Caret comes from selectionStart.
              const mentionActive = handleMentionInput(value, e.target.selectionStart ?? value.length);

              // Command autocomplete logic — skipped while a mention is active.
              if (!mentionActive && value.startsWith('/') && !value.startsWith('//')) {
                const prefix = value.slice(1).toLowerCase();
                const matches = getMatchingCommands(prefix);
                setCommandMatches(matches);
                setShowCommandDropdown(matches.length > 0);
                setSelectedCommandIndex(0);
              } else {
                setShowCommandDropdown(false);
                setCommandMatches([]);
              }

              // Auto-resize on every change
              requestAnimationFrame(resizeTextarea);
            }}
            placeholder={onboardingActive ? 'Tap through the quick tour above — chat unlocks when it’s done' : isAnyListening ? (activeInterimTranscript ? '' : 'Listening...') : placeholder}
            className="chat-input"
            disabled={onboardingActive || !activeConversation || hasNoConversations || (isLoading && !isStreaming) || isReadOnly}
            autoFocus
            autoComplete="off"
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onFocus={() => {
              if (isMobile && shouldShowAnyPane) {
                setMobileView('chat');
              }
            }}
          />
          {/* Upload button - bottom left (triggers import) */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            style={{ display: 'none' }}
            multiple
            accept=".vcf,.ics,.csv,.xlsx,.xls,.docx,.doc,.json,.md,.txt,.pdf,image/*,.jpg,.jpeg,.png,.gif,.webp,.svg,.heic,.heif"
          />
          <button
            type="button"
            className="chat-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            title="Upload file"
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"></path>
            </svg>
          </button>
          {/* Clear button moved to header */}
          {/* Voice input: waveform when listening, mic button when idle */}
          {isAnyListening ? (
            <>
              <div className="chat-dictation-waveform">
                <AudioWaveform analyser={analyser} height={36} />
              </div>
              <button
                type="button"
                className="chat-mic-btn cancel"
                onClick={cancelVoiceInput}
                title="Cancel dictation"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </>
          ) : (
            <button
              type="button"
              className="chat-mic-btn"
              {...micGesture}
              disabled={onboardingActive || !activeConversation || hasNoConversations || (isLoading && !isStreaming) || isReadOnly}
              title="Tap: voice input | Hold or double-tap: voice conversation"
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </svg>
            </button>
          )}
          {(isStreaming || (voiceMode.isActive && voiceMode.phase === 'speaking')) ? (
            <button
              type="button"
              className="chat-send-btn stop-btn"
              onClick={() => {
                // Stop both: cancel SSE if still streaming, and halt TTS if
                // the agent is mid-speech (SSE may have already finished).
                if (isStreaming) {
                  stopStreaming();
                }
                if (voiceMode.isActive && voiceMode.phase === 'speaking') {
                  voiceMode.stopSpeaking();
                }
                // If stopped within 5 seconds of sending, repopulate input for editing
                const last = lastSentRef.current;
                if (last && Date.now() - last.at < 5000) {
                  setInputValue(last.text);
                  lastSentRef.current = null;
                  setTimeout(() => {
                    if (chatInputRef.current) {
                      chatInputRef.current.focus();
                      // Place cursor at end
                      chatInputRef.current.selectionStart = chatInputRef.current.value.length;
                      chatInputRef.current.selectionEnd = chatInputRef.current.value.length;
                    }
                  }, 50);
                }
              }}
              title="Stop"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                stroke="none"
              >
                <rect x="6" y="6" width="12" height="12" rx="2" />
              </svg>
            </button>
          ) : (
            <button
              type="submit"
              className={`chat-send-btn ${(inputValue.trim() || pendingAttachments.length > 0) ? 'has-content' : 'empty'}`}
              disabled={onboardingActive || !activeConversation || hasNoConversations || isLoading || isImporting || (!inputValue.trim() && pendingAttachments.length === 0) || isReadOnly}
              title="Send message"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="currentColor"
                stroke="none"
              >
                <path d="M12 2L12 18M12 2L6 8M12 2L18 8" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
              </svg>
            </button>
          )}
        </div>
        </form>
  );
}
